import { type FrameBuilderConfigurationWithLimits } from './configuration'
import { clamp } from './helpers'
import { getSanityImageUrl } from './image'
import { type FrameBuilderState } from './state'

export interface FrameTexture {
  diffuseUrl: string
  normalUrl?: string
}

export interface FrameSettings {
  artWidth: number
  artHeight: number
  borderWidth: number
  passepartoutWidth: number
  depth: number
  inset: number
  passepartoutColor: string
  artworkUrl?: string
  backgroundColor?: string
  frameTexture?: FrameTexture
}

/**
 * Determines if size is valid and returns it, otherwise returns default size.
 */
const getSize = (
  size: number | undefined,
  defaultSize: number,
  allowZero = false,
) => {
  // If size is undefined or zero and (zeros aren't allowed or size is not zero)
  if (!size && (!allowZero || size !== 0)) {
    return defaultSize
  }

  return size
}

/**
 * Gets frame settings from state.
 */
export const getFrameSettings = (
  state: FrameBuilderState,
  defaultState: FrameBuilderState,
  configuration: FrameBuilderConfigurationWithLimits,
): FrameSettings => {
  const inputWidth = getSize(state.artwork.width, defaultState.artwork.width)
  const inputHeight = getSize(state.artwork.height, defaultState.artwork.height)
  const artWidth = clamp(
    inputWidth,
    configuration.limits.minimumSize,
    Math.max(
      configuration.limits.maximumWidth,
      configuration.limits.maximumHeight,
    ),
  )
  const artHeight = clamp(
    inputHeight,
    configuration.limits.minimumSize,
    Math.max(
      configuration.limits.maximumWidth,
      configuration.limits.maximumHeight,
    ),
  )

  const borderWidth = getSize(
    state.profile?.width,
    defaultState.profile?.width ?? 0,
  )
  const profileThickness = getSize(
    state.profile?.thickness,
    defaultState.profile?.thickness ?? 0,
  )
  const glassThickness = getSize(
    state.glass?.thickness,
    defaultState.glass?.thickness ?? 0,
  )
  const passepartoutWidth = getSize(
    state.passepartout.size?.size,
    defaultState.passepartout.size?.size ?? 0,
    true,
  )
  const passepartoutColor =
    state.passepartout.color?.color?.hex ??
    defaultState.passepartout.color?.color?.hex

  const artworkUrl = state.artwork.image?.url ?? defaultState.artwork.image?.url
  const backgroundColor =
    state.artwork.backgroundColor?.current ??
    defaultState.artwork.backgroundColor?.current

  const texture = state.texture?.image ?? defaultState.texture?.image

  return {
    artWidth: artWidth / 100,
    artHeight: artHeight / 100,
    borderWidth: borderWidth / 1000,
    depth: profileThickness / 1000,
    inset: glassThickness / 1000,
    passepartoutWidth: passepartoutWidth / 100,
    passepartoutColor: passepartoutColor ?? '',
    artworkUrl,
    backgroundColor,
    frameTexture: {
      diffuseUrl: getSanityImageUrl(texture) ?? '',
    },
  }
}
